import React, { useEffect, useMemo, useState } from "react";
import useAutoScroll from "../../hooks/useAutoScroll";
import { Box, Typography } from "@mui/material";
import CardMusicPlayer from "./CardMusicPlayer";
import MainPageCard from "./MainPageCard";
import WeddingDetails from "./WeddingDetails";
import CardBottomNav from "./CardBottomNav";
import CountingDay from "./CountingDay";
import GalleryCarousel from "./GalleryCarousel";
import WishSection from "./WishSection";
import "../../CSS/rsvpPopup.css";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CardOpening from "../CardView/CardOpening";
import { useParams } from "react-router-dom";
import { getCardById, getProgrammes, getContacts, getCardImages, listenToGifts } from "../../services/cardService";
import { WhatsApp } from "@mui/icons-material";
import CardParticles from "./CardParticles";
import LoadingScreen from "../LoadingScreen";


const ActualCardRender = () => {

    const { cardId } = useParams();
    const [cardData, setCardData] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [images, setImages] = useState([]);
    const [gifts, setGifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cardOpened, setCardOpened] = React.useState(false);
    const [programmes, setProgrammes] = useState([]);
    const [musicReady, setMusicReady] = useState(false);
    useAutoScroll(cardOpened);

    const theme = useMemo(() => createTheme({
        typography: {
            fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
        },
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: cardData?.themeColour,
                            },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: cardData?.themeColour,
                        },
                    },
                },
            },
        },
    }), [cardData?.themeColour]);



    useEffect(() => {
        const navbar = document.querySelector(".main-navbar");
        const footer = document.querySelector(".main-footer"); // if you also add a class to footer

        if (navbar) navbar.style.display = "none";
        if (footer) footer.style.display = "none";

        return () => {
            if (navbar) navbar.style.display = "";
            if (footer) footer.style.display = "";
        };
    }, []);


    /* --------------------------------
    Fetch ALL card data
 -------------------------------- */
    useEffect(() => {
        if (!cardId) return;

        const fetchCardData = async () => {
            try {
                const [card, programmesData, contactsData, imagesData] = await Promise.all([
                    getCardById(cardId),
                    getProgrammes(cardId),
                    getContacts(cardId),
                    getCardImages(cardId),
                ]);

                setCardData(card);
                setProgrammes(programmesData);
                setContacts(contactsData);
                setImages(imagesData);
            } catch (err) {
                console.error(err);
                setCardData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchCardData();
    }, [cardId]);

    useEffect(() => {
        if (!cardId) return;

        const unsubscribe = listenToGifts(cardId, (fetchedGifts) => {
            setGifts(fetchedGifts);
        });

        return () => unsubscribe();
    }, [cardId]);

    // If no music URL is set, skip waiting for YouTube to load
    useEffect(() => {
        if (cardData && !cardData.musicYoutubeURL) {
            setMusicReady(true);
        }
    }, [cardData]);

    const designTemplate = cardData?.designTemplate;
    const isClassicTemplate = designTemplate === "classic";
    const designCode = cardData?.designCode;
    console.log("designCode", designCode);

    /* --------------------------------
       Loading / Error states
    -------------------------------- */
    if (loading) {
        return <LoadingScreen />;
    }

    if (!cardData) {
        return <Typography align="center">Card not found</Typography>;
    }

    return (
        <ThemeProvider theme={theme}>
            <div
                style={{
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    overflowY: "auto",
                }}
            >

                <div
                    style={{
                        position: 'relative',  // scopes CardParticles (absolute) to card column
                        width: "100%",
                        maxWidth: "450px",
                        ...(isClassicTemplate
                            ? {
                                backgroundImage: `linear-gradient(rgba(226, 128, 47, 0.38)), url(/card-backgrounds/klasikFullBackground.jpg)`,
                                backgroundSize: "100% 100%",
                            }
                            : {
                                backgroundColor: cardData.themeBaseCardColour,
                            }
                        ),
                        boxShadow: "0 0 10px rgba(165, 13, 13, 0.1)",
                    }}
                >
                    {/* Particles scoped inside the card column */}
                    <CardOpening data={cardData} openingStyle={cardData.openingStyle ?? 'Tingkap A'} openingColor={cardData.openingColor ?? '#f2f2f2'} onOpen={() => setCardOpened(true)} musicReady={musicReady}>
                        <CardParticles data={cardData} />
                        <CardMusicPlayer data={cardData} playOnOpen={cardOpened} onMusicReady={() => setMusicReady(true)} />
                        <MainPageCard data={cardData} isOpened={cardOpened} />
                        <CardBottomNav data={cardData} contacts={contacts} gifts={gifts} />
                    </CardOpening>

                    {cardOpened && (
                        <>
                            <WeddingDetails data={cardData} programmes={programmes} />
                            <CountingDay data={cardData} images={images} />
                            {images.length > 0 &&
                                <GalleryCarousel images={images} />
                            }
                            <WishSection data={cardData} source="firebase" />
                            <Box sx={{ px: 2, textAlign: "center", pb: "110px", display: "block" }}>
                                <Typography sx={{ fontFamily: "Poppins", fontSize: "13px" }} color={designCode === "floral-007" ? "white" : "text.secondary"}>Created by</Typography>
                                <Box sx={{ justifyContent: "center", display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Typography sx={{ fontFamily: "Poppins", color: cardData.themeColour, fontWeight: "bold" }}>Sejiwa ❤️</Typography>
                                    <WhatsApp onClick={() => window.open("https://wa.me/60134149684", "_blank")} sx={{ cursor: "pointer", ":hover": { color: cardData.themeColour }, fontSize: "20px", color: '#208836ff', fontWeight: "bold" }} />
                                </Box>

                            </Box>
                        </>
                    )}


                </div>
            </div>
        </ThemeProvider>
    );
};

export default ActualCardRender;
